import { useState, type ChangeEvent, type FormEvent } from 'react';
import { USERNAME_RE } from '@blackblack/shared';
import { ApiError, errorMessage } from '../../lib/api';
import { Link } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { Button, TextField } from '../../components/ui/primitives';
import styles from './auth.module.css';

export function LoginForm({ onDone, onForgot }: { onDone?: () => void; onForgot?: () => void }) {
  const { login } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(identifier, password);
      onDone?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <TextField label="E-mail of gebruikersnaam" value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" autoCapitalize="none" required />
      <TextField label="Wachtwoord" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <Button type="submit" size="lg" block loading={busy} disabled={!identifier || !password}>
        Inloggen
      </Button>
      {onForgot && (
        <button type="button" className={styles.link} onClick={onForgot}>
          Wachtwoord vergeten?
        </button>
      )}
    </form>
  );
}

export function RegisterForm({ onDone }: { onDone?: () => void }) {
  const { register } = useAuth();
  const [form, setForm] = useState({ displayName: '', username: '', email: '', password: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: k === 'username' ? e.target.value.toLowerCase().replace(/\s/g, '') : e.target.value }));

  const localErrors: Record<string, string> = {};
  if (form.username && !USERNAME_RE.test(form.username)) localErrors.username = '2–24 tekens: a-z, 0-9, _ en .';
  if (form.password && form.password.length < 8) localErrors.password = 'Minimaal 8 tekens';
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email)) localErrors.email = 'Ongeldig e-mailadres';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (Object.keys(localErrors).length) return;
    setBusy(true);
    setError(null);
    setErrors({});
    try {
      await register({ ...form, displayName: form.displayName || undefined } as typeof form);
      onDone?.();
    } catch (err) {
      if (err instanceof ApiError && err.details) {
        const d = err.details;
        setErrors(Object.fromEntries(Object.entries(d).map(([k, v]) => [k, v === 'taken' ? (k === 'email' ? 'Dit e-mailadres is al in gebruik' : 'Deze gebruikersnaam is al bezet') : v])));
      }
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const err = (k: string) => errors[k] ?? localErrors[k];

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <TextField label="Naam" value={form.displayName} onChange={set('displayName')} autoComplete="name" maxLength={50} placeholder="Hoe mogen we je noemen?" />
      <TextField label="Gebruikersnaam" value={form.username} onChange={set('username')} autoComplete="username" autoCapitalize="none" maxLength={24} error={err('username')} hint="Dit is je @naam op BlackBlack" required />
      <TextField label="E-mail" type="email" value={form.email} onChange={set('email')} autoComplete="email" error={err('email')} required />
      <TextField label="Wachtwoord" type="password" value={form.password} onChange={set('password')} autoComplete="new-password" error={err('password')} hint="Minimaal 8 tekens" required />
      {error && !Object.keys(errors).length && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <Button type="submit" size="lg" block loading={busy} disabled={!form.username || !form.email || !form.password}>
        Account maken
      </Button>
      <p className={styles.legal}>
        Door een account te maken ga je akkoord met de <Link to="/voorwaarden">voorwaarden</Link> en het <Link to="/privacy">privacybeleid</Link> van BlackBlack. Je moet minimaal 16 jaar zijn.
      </p>
    </form>
  );
}
